import { useEffect, useState } from 'react';
import { Grid, Tab, Tabs } from '@mui/material';
import { LoadingButton } from '@mui/lab';

import CategoryCard from './CategoryCard';
import AddEditCategory from './AddEditCategory';
import CustomModal from '../../comman/CustomModal';
import NoData from '../../comman/NoData';

import { apiRequest } from '../../../utils/config/apiRequest';
import { apiUrl } from '../../../utils/endPoints/apiUrl';
import { formatFileURL } from '../../../utils/helper/appHelpers';
import { showMessage } from '../../../utils/toast/globalToast';
import { _toastVariants, FILE_UPLOAD_DIR } from '../../../utils/Constant';

const MODAL = {
  SELECT_CATEGORIES: 'SELECT_CATEGORIES',
};

const TABS = ['Default Services', 'Self Services'];

export default function CategoryListing({
  serviceId,
  shopServiceId,
  shopId,
  userId,
  onClose = () => { },
}) {
  const [categories, setCategories] = useState([]);
  const [defaultMap, setDefaultMap] = useState({});
  const [customMap, setCustomMap] = useState({});
  const [modal, setModal] = useState({ name: '', data: null });
  const [tab, setTab] = useState(0);

  /* ------------------ MODAL HELPERS ------------------ */
  const openModal = (name, data) => setModal({ name, data });
  const closeModal = () => setModal({ name: '', data: null });

  /* ------------------ INITIAL LOAD ------------------ */
  useEffect(() => {
    fetchCategories();
    fetchShopCategories();
  }, []);

  const fetchCategories = async () => {
    const { data, status } = await apiRequest({
      endUrl: apiUrl.masters.serviceCategories(serviceId),
      query: { paginate: 0, serviceId },
    });

    if (status) setCategories(data || []);
  };

  const fetchShopCategories = async () => {
    closeModal();

    const { data, status } = await apiRequest({
      endUrl: apiUrl.branch.serviceCategoryListing(shopId, shopServiceId),
      query: { paginate: 0 },
    });

    if (!status) return;

    const defaults = {};
    const customs = {};

    data?.rows?.forEach(item => {
      if (item.serviceCategoryId) {
        defaults[item.serviceCategoryId._id] = {
          ...item,
          serviceCategoryId: item.serviceCategoryId._id,
          name: item.name ?? item.serviceCategoryId.name,
          thumbnailURL: item.thumbnailURL ?? item.serviceCategoryId.thumbnailURL,
          remarks: item.remarks?.length ? item.remarks : item.serviceCategoryId.remarks || [],
          aboutService: item.aboutService?.length ? item?.aboutService : item.serviceCategoryId.aboutService || [],
        };
      } else {
        customs[item._id] = item;
      }
    });

    setDefaultMap(defaults);
    setCustomMap(customs);
  };

  /* ------------------ SELECTION LOGIC ------------------ */
  const toggleCategory = (service, isDefault = true) => {
    const setMap = isDefault ? setDefaultMap : setCustomMap;

    setMap(prev => {
      const next = { ...prev };
      const existing = next[service._id];

      // remove newly added
      if (existing && !existing._id) {
        delete next[service._id];
        return next;
      }

      // toggle remove / restore
      if (existing?._id) {
        next[service._id] = {
          ...existing,
          action: existing.action === 'REMOVE' ? undefined : 'REMOVE',
          ...(isDefault && { serviceCategoryId: service._id }),
        };
        return next;
      }

      // add new
      const { _id, ...rest } = service;
      next[service._id] = {
        ...rest,
        serviceCategoryId: isDefault ? service._id : undefined,
        action: 'ADD',
      };

      return next;
    });
  };

  /* ------------------ CUSTOMIZE SAVE ------------------ */
  const handleCustomize = service => {
    closeModal();
    const setSource = tab === 0 ? setDefaultMap : setCustomMap;
    const key = tab === 0 ? service.serviceCategoryId : service._id;

    setSource(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        ...service,
        action: 'ADD',
      },
    }));
  };

  /* ------------------ PAYLOAD BUILDER ------------------ */
  const buildPayload = async items => {
    const payload = [];

    for (const item of items) {
      if (item.action === 'REMOVE') {
        payload.push({
          shopServiceCategoryId: item._id,
          action: 'REMOVE',
        });
        continue;
      }

      if (!item.servicePrice || !item.discountedPrice) {
        throw new Error('Please setup price and discounted price');
      }

      let thumbnailURL = item.thumbnailURL;

      if (typeof thumbnailURL !== 'string' && thumbnailURL) {
        const formData = new FormData();
        formData.append('file', thumbnailURL);

        const { data, status } = await apiRequest({
          method: 'POST',
          endUrl: apiUrl.uploadFile(FILE_UPLOAD_DIR.SHOP),
          body: formData,
          headerType: 'form',
        });

        if (status) thumbnailURL = data?.shortUrl;
      }

      if (item.action === "ADD") {
        payload.push({
          name: item.name,
          shopKeeperId: userId,
          shopServiceCategoryId: item._id,
          serviceCategoryId: item.serviceCategoryId,
          remarks: item.remarks,
          thumbnailURL,
          onsite: item.onsite || false,
          offsite: item.offsite || false,
          serviceTimeInMin: item.serviceTimeInMin || 0,
          servicePrice: item.servicePrice,
          discountedPrice: item.discountedPrice,
          aboutService: item.aboutService || [],
          serviceProcessSteps: item.serviceProcessSteps || [],
          onsiteVisitPrice: item.onsiteVisitPrice || 0,
          additionalService: item?.additionalService ?item?.additionalService?.map(item => ({
            serviceId: item?._id,
            name: item?.name,
            price: item?.price,
          })) : [],
          urgentCharge: item?.urgentCharge || 0,
          sizeStandard: item?.sizeStandard ? item?.sizeStandard?.map(item => ({
            countryId: item?._id,
            country: item?.country,
            lenInMM: item?.lenInMM,
            widthInMM: item?.widthInMM
          })) : [],
          action: 'ADD',
        });
      }
    }

    return payload;
  };

  /* ------------------ SAVE HANDLER ------------------ */
  const handleSave = async () => {
    try {
      const source = tab === 0 ? defaultMap : customMap;
      const payload = await buildPayload(Object.values(source));

      const { status } = await apiRequest({
        method: 'POST',
        endUrl: apiUrl.branch.addEditServiceCategory(shopId, shopServiceId),
        body: payload,
        showMsg: true,
      });

      if (status) onClose();
    } catch (err) {
      showMessage({
        message: err.message,
        variant: _toastVariants.Error,
      });
    }
  };

  /* ------------------ RENDER ------------------ */
  const activeMap = tab === 0 ? defaultMap : customMap;

  const isDisabled = () => {
    const source = tab === 0 ? defaultMap : customMap;
    return !Object.values(source)?.some(item => ['ADD', 'REMOVE']?.includes(item?.action))
  }

  return (
    <>
      <Grid container spacing={2}>
        <Grid item md={10}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable">
            {TABS.map(label => (
              <Tab key={label} label={label} />
            ))}
          </Tabs>
        </Grid>

        <Grid item md={2} textAlign="right">
          {tab === 1 && (
            <LoadingButton
              size="small"
              variant="contained"
              onClick={() => openModal(MODAL.SELECT_CATEGORIES, { flow: 'ADD' })}
            >
              Add New
            </LoadingButton>
          )}
        </Grid>

        {tab === 0 &&
          categories.map(service => (
            <Grid key={service._id} item md={6}>
              <CategoryCard
                name={service.name}
                imgUrl={formatFileURL(
                  defaultMap[service._id]?.thumbnailURL ||
                  service.thumbnailURL
                )}
                remarks={
                  defaultMap[service._id]?.remarks || service.remarks || []
                }
                isSelected={
                  defaultMap[service._id]?.action === 'ADD' ||
                  (defaultMap[service._id]?.action !== 'REMOVE' &&
                    Boolean(defaultMap[service._id]?.serviceCategoryId))
                }
                onCustomize={() =>
                  openModal(MODAL.SELECT_CATEGORIES, defaultMap[service._id])
                }
                onSelect={() => toggleCategory(service, true)}
              />
            </Grid>
          ))}

        {tab === 1 &&
          Object.values(customMap).map(service => (
            <Grid key={service._id} item md={6}>
              <CategoryCard
                name={service.name}
                imgUrl={formatFileURL(service.thumbnailURL)}
                remarks={service.remarks || []}
                isSelected={service.action !== 'REMOVE'}
                onCustomize={() =>
                  openModal(MODAL.SELECT_CATEGORIES, service)
                }
                onSelect={() => toggleCategory(service, false)}
              />
            </Grid>
          ))}

        <Grid item md={12} display={'flex'} justifyContent={'center'}>
          {Object.values(activeMap).length ? (
            <LoadingButton
              sx={{ width: 200 }}
              size="large"
              variant="contained"
              onClick={handleSave}
              disabled={isDisabled()}
            >
              Save
            </LoadingButton>
          ) : (
            <NoData />
          )}
        </Grid>
      </Grid>

      <CustomModal
        width={60}
        open={modal.name === MODAL.SELECT_CATEGORIES}
        title={
          modal?.data?.flow === 'ADD'
            ? 'Add New'
            : modal?.data?.name ||
            modal?.data?.serviceCategoryId?.name
        }
        onClose={closeModal}
      >
        <AddEditCategory
          defaultData={modal.data}
          serviceCategoryId={modal?.data?.serviceCategoryId}
          onSave={handleCustomize}
          onCreate={fetchShopCategories}
          shopKeeperId={shopId}
          shopId={shopId}
          shopServiceId={shopServiceId}
          userId={userId}
        />
      </CustomModal>
    </>
  );
}
